import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { HandHeart } from 'lucide-react'
import { useState } from 'react'
import { claimCover, coverWhen, fetchOpenCover } from '../api/cover'
import { useCan } from '../useSession'
import CoverEveningSheet from './CoverEveningSheet'

/**
 * The nights nobody has, offered to anybody who could take one (D-650).
 *
 * **Owner, from the pilot:** *"Don't want the needs coverage to email everyone.
 * Just put it out there for anyone to take as coverage. Maybe show on all
 * teachers home page."*
 *
 * Until this existed, marking a session *needs cover* notified nobody and
 * opened it to nobody — it put a row on Tonya's admin queue and stopped there.
 * A teacher who would happily have taken a Tuesday had no way to learn one was
 * going, and the absent teacher's only route was naming colleagues one at a
 * time and waiting for each to answer.
 *
 * **No message goes out, and that is the request rather than a shortcut.** A
 * board is read by somebody who came looking; a fan-out reaches seventeen
 * people to find one, which is the fatigue D-554 spends all its rules avoiding.
 * It sits on the screen she already opens (D-365), which is what makes coming
 * looking cost nothing.
 *
 * **Below her own cover, never above it.** What is waiting on *her* comes
 * first; this is an offer, and an offer that outranks an obligation is how a
 * screen teaches people to scroll past the top of it.
 */

export default function OpenCoverOnHome() {
  const queryClient = useQueryClient()

  // A parent has no cover to take and the endpoint refuses her; asking anyway
  // would put a 403 in her console on every visit to Home.
  const canCover = useCan('BookRooms')

  const { data } = useQuery({
    queryKey: ['cover', 'open'],
    queryFn: fetchOpenCover,
    enabled: canCover,
  })

  /*
   * **`Array.isArray`, not `?? []`, and it is not defensive programming.**
   *
   * What she came to Home for is tonight's classes, and this panel must never
   * be able to take that down (D-209). `?? []` only covers the value being
   * absent; anything else answering this route - a proxy, an error page, a
   * shape that changed - reaches `.map` and throws inside render, which blanks
   * the whole screen rather than this box. `observed`: it did exactly that to
   * five tests on three unrelated Home screens.
   *
   * Nothing to show and a lookup that went wrong both render nothing, which is
   * the honest reading of "we do not know" - a heading over an empty box reads
   * as *nobody needs you*.
   */
  const rows = Array.isArray(data) ? data : []

  /*
   * Everything a claim can have changed: the board itself, her own cover
   * panel and her week (the class joins it), the one ask list (a claim *is*
   * an ask, and a student teacher's lands on the studio's), and the bell.
   */
  const settle = () => {
    for (const key of [
      ['cover'],
      ['asks'],
      ['notifications'],
      ['teacher-home'],
      ['teacher-day'],
      ['calendar'],
      ['registers'],
    ]) {
      queryClient.invalidateQueries({ queryKey: key })
    }
  }

  /*
   * **Keyed by row, so one failure names one row.** A single shared mutation
   * would put "you are busy then" under every class on the board, and the one
   * sentence the server writes is about the one she tapped.
   */
  const claim = useMutation({
    mutationFn: (id: string) => claimCover(id),
    onSuccess: () => {
      settle()
      // Whatever she has just taken, the sheet about it is now about the past.
      setOpened(null)
    },
    // A refusal means the board is stale — somebody took it, or she has since
    // been booked — so it is redrawn before she taps again (D-436).
    onError: settle,
  })

  /*
   * **Which row she has opened, if any** (D-776).
   *
   * The row still carries its own *Take it*, so nothing she could do before
   * needs a tap more than it did. This is for the decision she could not make
   * from the row at all: what else she has that evening.
   */
  const [opened, setOpened] = useState<string | null>(null)

  /*
   * **What just happened, because the row is about to vanish either way.**
   *
   * A claim that is accepted outright is self-evident: the class appears on her
   * week. The student teacher from outside the class is the one who needs
   * telling — her yes is with the studio (D-540), so the row disappears, no
   * substitute is written, no message is sent, and without this the only
   * feedback is the row going. She can find it again on `/asks`, but the moment
   * she is looking at the screen is this one.
   *
   * Kept outside the list so it survives the row being removed by the refetch.
   */
  const waiting = claim.isSuccess && claim.data?.state === 'AwaitingApproval'

  // Nothing going, so nothing is said. No empty heading - unless she has just
  // taken the last one and is owed the sentence below.
  if (rows.length === 0 && !waiting) return null

  return (
    <section
      aria-labelledby="open-cover-on-home"
      className="mb-4 rounded-xl border border-line bg-surface px-4 py-3"
    >
      <h2
        id="open-cover-on-home"
        className="mb-2 flex items-center gap-1.5 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase"
      >
        <HandHeart size={13} strokeWidth={2.5} />
        Classes needing cover
      </h2>

      {waiting && (
        <p className="mb-2 rounded-lg border border-warn bg-warn-bg px-2.5 py-2 text-[12px] text-ink-2">
          You have said yes &mdash; waiting for the studio to agree.
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {rows.map((row) => {
          const failed = claim.isError && claim.variables === row.id
          const saving = claim.isPending && claim.variables === row.id

          return (
            <li
              key={row.id}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-line pt-2 first:border-t-0 first:pt-0"
            >
              <span className="min-w-0 flex-1">
                {/*
                  **The name opens her evening** (D-776), rather than the whole
                  row doing it: this row already carries a control on its right,
                  and a row-wide tap that has to exempt its own button is the
                  arrangement that fired on a dialog it had just opened (D-768).

                  `wrap-anywhere` because a Jackrabbit name is one unbreakable
                  word to CSS and the distinguishing part is at the end (D-115).
                */}
                <button
                  type="button"
                  onClick={() => setOpened(row.id)}
                  aria-label={`Your evening around ${row.className}`}
                  // 44px declared rather than inferred from the line height (D-601).
                  className="block min-h-[44px] w-full text-left"
                >
                  <span className="block text-[12.5px] font-semibold wrap-anywhere text-ink-2 underline decoration-line-2 decoration-dotted underline-offset-2 hover:text-accent-2 hover:decoration-accent">
                    {row.className}
                  </span>
                  <span className="block text-[12px] text-ink-3">
                    {coverWhen(row)} &middot; {row.absentName} is out
                  </span>
                </button>

                {/*
                  What the person picking it up should know (D-364) — the one
                  thing on this row that helps her decide, so it is not behind
                  a tap.
                */}
                {row.note && (
                  <span className="mt-0.5 block text-[12px] wrap-anywhere text-ink-3 italic">
                    &ldquo;{row.note}&rdquo;
                  </span>
                )}

                {failed && (
                  <span className="mt-0.5 block text-[12px] text-crit">
                    {claim.error instanceof Error
                      ? claim.error.message
                      : 'That one could not be taken.'}
                  </span>
                )}
              </span>

              {/*
                **A clash is a sentence, never a disabled button with no
                reason** — she would read it as the app being broken. The
                server refuses it too (D-650), so this is not the only guard.
              */}
              {row.clashes ? (
                <span className="shrink-0 text-[12px] text-ink-3">You are busy then</span>
              ) : (
                <button
                  type="button"
                  onClick={() => claim.mutate(row.id)}
                  disabled={saving}
                  /*
                    **Named, because the visible words cannot be.** Sixty rows
                    of *Take it* are sixty identically-named controls to a
                    screen reader, and a Jackrabbit name is up to 69 characters
                    (D-115) — so it goes in the label and not on the button.
                  */
                  aria-label={`Take ${row.className}`}
                  /*
                    **Declared, not inferred from the padding** (D-601). The
                    padding here computes to about 40px, and every other control
                    in this codebase claiming 44 says so with this class - the
                    comment that merely asserts a height is the one that goes
                    stale the first time somebody changes the text size.
                  */
                  className="min-h-[44px] shrink-0 rounded-lg border border-accent bg-accent-soft px-3 py-2.5 text-[12.5px] font-semibold text-accent-2 hover:border-ink-3 disabled:opacity-60"
                >
                  {saving ? 'Taking...' : 'Take it'}
                </button>
              )}
            </li>
          )
        })}
      </ul>

      {/*
        **Her evening around the one she tapped** (D-776).

        Rendered from the row's id rather than the row, so a refetch that
        reorders or drops the board cannot leave the sheet describing something
        that is no longer on it — the sheet asks the server for itself, and a
        night somebody else has just taken 404s rather than rendering stale.
      */}
      {opened && (
        <CoverEveningSheet
          absenceId={opened}
          onDismiss={() => setOpened(null)}
          onTake={() => claim.mutate(opened)}
          taking={claim.isPending && claim.variables === opened}
          error={
            claim.isError && claim.variables === opened
              ? claim.error instanceof Error
                ? claim.error.message
                : 'That one could not be taken.'
              : null
          }
        />
      )}
    </section>
  )
}
